import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, Button } from '@alertwhale/ui'
import {
  Bell,
  Activity,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowUpRight,
  TrendingUp,
  Zap,
  Clock,
  Send,
  AppWindow,
  Key,
  BarChart3,
  BookOpen,
  Plus,
  RefreshCw,
} from 'lucide-react'
import { formatNumber, cn } from '@/lib/utils'
import { useDashboardStats } from '@/hooks/useDashboardStats'
import { useNotifications } from '@/hooks/useNotifications'
import { useLiveSessions } from '@/hooks/useLiveSessions'
import { useApplications } from '@/hooks/useApplications'
import { Link, useNavigate } from 'react-router-dom'
import { SendNotificationDialog } from '@/components/notifications/SendNotificationDialog'

export function DashboardPage() {
  const navigate = useNavigate()
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useDashboardStats()
  const { data: recentNotifications, refetch: refetchNotifications } = useNotifications({ limit: 5 })
  const { data: activeSessions, refetch: refetchSessions } = useLiveSessions({ status: 'active', limit: 5 })
  const { data: applications } = useApplications()

  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await Promise.all([refetchStats(), refetchNotifications(), refetchSessions()])
    setIsRefreshing(false)
  }

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <div className="absolute inset-0 h-10 w-10 animate-ping opacity-20 rounded-full bg-primary" />
          </div>
          <span className="text-sm text-muted-foreground">Loading dashboard...</span>
        </div>
      </div>
    )
  }

  const deliveryRate = ((stats?.delivery_rate || 1) * 100).toFixed(1)
  const hasApps = applications && applications.length > 0

  return (
    <>
      <div className="space-y-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-gradient">Command Center</span>
            </h1>
            <p className="text-muted-foreground">
              Real-time overview of your notification infrastructure
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
              Refresh
            </Button>
            <Button
              variant="glow"
              size="sm"
              onClick={() => setSendDialogOpen(true)}
              disabled={!hasApps}
            >
              <Send className="h-4 w-4" />
              Send Notification
            </Button>
          </div>
        </div>

        {/* No Apps Warning */}
        {!hasApps && (
          <div className="glass-card rounded-xl p-5 border-warning/30 bg-warning/5">
            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-warning/20 text-warning">
                <AppWindow className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-warning">No Applications Yet</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Create your first application to start sending notifications.
                </p>
              </div>
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/applications')}
              >
                <Plus className="h-4 w-4" />
                Create App
              </Button>
            </div>
          </div>
        )}

        {/* Stats Grid - 4 Individual Cards */}
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
          {/* Notifications Today Card */}
          <Link to="/notifications" className="group block">
            <Card className="glass-card rounded-xl h-full transition-all duration-200 hover:border-primary/50 fade-in stagger-1">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-muted-foreground">Notifications Today</p>
                    <div className="space-y-1">
                      <p className="text-3xl font-bold tracking-tight group-hover:text-primary transition-colors">
                        {formatNumber(stats?.notifications_today || 0)}
                      </p>
                      <div className="flex items-center gap-1.5 text-success text-xs font-medium">
                        <TrendingUp className="h-3 w-3" />
                        <span>+12.5% from yesterday</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
                    <Bell className="h-6 w-6" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Total all time</span>
                    <span className="font-medium">{formatNumber(stats?.notifications_total || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Active Live Sessions Card */}
          <Link to="/live-sessions" className="group block">
            <Card className="glass-card rounded-xl h-full transition-all duration-200 hover:border-success/50 fade-in stagger-2">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-muted-foreground">Active Live Sessions</p>
                    <div className="space-y-1">
                      <p className="text-3xl font-bold tracking-tight group-hover:text-success transition-colors">
                        {formatNumber(stats?.active_live_sessions || 0)}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="flex items-center gap-1 text-success">
                          <span className="w-1.5 h-1.5 rounded-full bg-success pulse-live" />
                          Live now
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-success/10 text-success group-hover:bg-success/20 transition-colors">
                    <Activity className="h-6 w-6" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Across all apps</span>
                    <span className="text-primary font-medium group-hover:underline">View all →</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Delivery Rate Card */}
          <Link to="/analytics" className="group block">
            <Card className="glass-card rounded-xl h-full transition-all duration-200 hover:border-success/50 fade-in stagger-3">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-muted-foreground">Delivery Rate</p>
                    <div className="space-y-1">
                      <p className="text-3xl font-bold tracking-tight group-hover:text-success transition-colors">
                        {deliveryRate}%
                      </p>
                      <div className="flex items-center gap-1.5 text-success text-xs font-medium">
                        <CheckCircle className="h-3 w-3" />
                        <span>Excellent performance</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-success/10 text-success group-hover:bg-success/20 transition-colors">
                    <Zap className="h-6 w-6" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="progress-glow">
                    <div style={{ width: `${deliveryRate}%` }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Failed Deliveries Card */}
          <Link to="/analytics" className="group block">
            <Card className="glass-card rounded-xl h-full transition-all duration-200 hover:border-destructive/50 fade-in stagger-4">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-muted-foreground">Failed Deliveries</p>
                    <div className="space-y-1">
                      <p className="text-3xl font-bold tracking-tight group-hover:text-destructive transition-colors">
                        {formatNumber(stats?.failed_deliveries || 0)}
                      </p>
                      <div className={cn(
                        'flex items-center gap-1.5 text-xs font-medium',
                        (stats?.failed_deliveries || 0) > 0 ? 'text-destructive' : 'text-success'
                      )}>
                        {(stats?.failed_deliveries || 0) > 0 ? (
                          <>
                            <XCircle className="h-3 w-3" />
                            <span>Requires attention</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-3 w-3" />
                            <span>All good</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={cn(
                    'flex items-center justify-center w-12 h-12 rounded-xl transition-colors',
                    (stats?.failed_deliveries || 0) > 0
                      ? 'bg-destructive/10 text-destructive group-hover:bg-destructive/20'
                      : 'bg-success/10 text-success group-hover:bg-success/20'
                  )}>
                    <XCircle className="h-6 w-6" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Last 24 hours</span>
                    <span className="text-primary font-medium group-hover:underline">View details →</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Recent Activity */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Recent Notifications */}
          <Card className="glass-card border-0 fade-in stagger-5">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold">Recent Notifications</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/notifications')}
                className="text-primary hover:text-primary hover:bg-primary/10"
              >
                View all
                <ArrowUpRight className="ml-1 h-3 w-3" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {!recentNotifications || recentNotifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                      <Bell className="h-6 w-6 text-primary" />
                    </div>
                    <p className="text-sm font-medium">No notifications yet</p>
                    <p className="text-xs text-muted-foreground mt-1 mb-4">
                      Send your first notification to get started
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSendDialogOpen(true)}
                      disabled={!hasApps}
                    >
                      <Send className="h-4 w-4" />
                      Send Notification
                    </Button>
                  </div>
                ) : (
                  recentNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => navigate('/notifications')}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-white/[0.02] transition-colors group cursor-pointer"
                    >
                      <div className="relative">
                        <div className="w-2 h-2 rounded-full bg-success" />
                        <div className="absolute inset-0 w-2 h-2 rounded-full bg-success animate-ping opacity-75" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                          {notification.title}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs px-1.5 py-0.5 rounded bg-white/[0.05] text-muted-foreground">
                            {notification.event_type}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(notification.created_at).toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Active Live Sessions */}
          <Card className="glass-card border-0 fade-in stagger-6">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold">Active Live Sessions</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/live-sessions')}
                className="text-primary hover:text-primary hover:bg-primary/10"
              >
                View all
                <ArrowUpRight className="ml-1 h-3 w-3" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {!activeSessions || activeSessions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center mb-3">
                      <Activity className="h-6 w-6 text-success" />
                    </div>
                    <p className="text-sm font-medium">No active sessions</p>
                    <p className="text-xs text-muted-foreground mt-1 mb-4">
                      Live sessions will appear here when active
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/live-sessions')}
                    >
                      <Activity className="h-4 w-4" />
                      View All Sessions
                    </Button>
                  </div>
                ) : (
                  activeSessions.map((session) => (
                    <div
                      key={session.id}
                      onClick={() => navigate('/live-sessions')}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-white/[0.02] transition-colors group cursor-pointer"
                    >
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Activity className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                          {session.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs px-1.5 py-0.5 rounded bg-white/[0.05] text-muted-foreground">
                            {session.type}
                          </span>
                          <div className="flex-1 max-w-24">
                            <div className="progress-glow h-1">
                              <div style={{ width: `${Math.round(session.progress * 100)}%` }} />
                            </div>
                          </div>
                          <span className="text-xs font-medium text-primary">
                            {Math.round(session.progress * 100)}%
                          </span>
                        </div>
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <button
            onClick={() => navigate('/applications')}
            className="group text-left w-full"
          >
            <div className="glass-card rounded-xl p-4 hover:border-primary/30 transition-all cursor-pointer h-full">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <AppWindow className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium group-hover:text-primary transition-colors">Applications</p>
                  <p className="text-xs text-muted-foreground">Manage your apps</p>
                </div>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/api-keys')}
            className="group text-left w-full"
          >
            <div className="glass-card rounded-xl p-4 hover:border-accent/30 transition-all cursor-pointer h-full">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                  <Key className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="text-sm font-medium group-hover:text-accent transition-colors">API Keys</p>
                  <p className="text-xs text-muted-foreground">Manage access</p>
                </div>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/analytics')}
            className="group text-left w-full"
          >
            <div className="glass-card rounded-xl p-4 hover:border-success/30 transition-all cursor-pointer h-full">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center group-hover:bg-success/20 transition-colors">
                  <BarChart3 className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-sm font-medium group-hover:text-success transition-colors">Analytics</p>
                  <p className="text-xs text-muted-foreground">View metrics</p>
                </div>
              </div>
            </div>
          </button>

          <a
            href="https://docs.alertwhale.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group text-left w-full"
          >
            <div className="glass-card rounded-xl p-4 hover:border-warning/30 transition-all cursor-pointer h-full">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center group-hover:bg-warning/20 transition-colors">
                  <BookOpen className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <p className="text-sm font-medium group-hover:text-warning transition-colors">API Docs</p>
                  <p className="text-xs text-muted-foreground">Read the docs</p>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>

      {/* Send Notification Dialog */}
      <SendNotificationDialog
        open={sendDialogOpen}
        onOpenChange={setSendDialogOpen}
      />
    </>
  )
}
